import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import DeckGL from '@deck.gl/react';
import { IconLayer, LineLayer, PathLayer, ScatterplotLayer } from '@deck.gl/layers';
import Map from 'react-map-gl/maplibre';
import 'maplibre-gl/dist/maplibre-gl.css';
import { netlabApi } from './api';
import {
  DRAFT_PIN, FILTERS, SELECTION_RING, hexToRgb, iconUrl, kindColor, kindLabel, matchesFilter, voltageColor,
} from './assets';
import { fitBounds, keyOf, normalizeView } from './geo';
import AddAssetForm from './AddAssetForm';
import SelectionPanel from './SelectionPanel';
import { PowerFlowLegend, busSeverity, describeResult, indexResults, loadingSeverity, severityColor } from './powerflow';

const MAP_STYLE = 'https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json';
const LOGO_URL = process.env.PUBLIC_URL + '/netlab_logo.svg';
const OFF_ALPHA = 38;
const UTM_CHOICES = [['EPSG:32632', 'UTM 32N'], ['EPSG:32633', 'UTM 33N'], ['EPSG:32634', 'UTM 34N'], ['EPSG:4326', 'lon/lat']];

// Icon size (px) by zoom: dots when zoomed out, readable badges when zoomed in.
const iconSizeFor = (zoom) => (zoom >= 17.5 ? 26 : zoom >= 16 ? 20 : zoom >= 15 ? 14 : zoom >= 14 ? 9 : 5);

function iconDef(url, key) {
  return { url, id: key, width: 64, height: 64, anchorY: 32 };
}

function Tooltip({ hover, pf }) {
  if (!hover) return null;
  const o = hover.object;
  const rows = [];
  const pfRow = pf && ['bus', 'line', 'trafo'].includes(o.table) ? describeResult(o.table, pf[o.table].get(o.id)) : null;
  if (o.table === 'bus') rows.push(`${o.vn_kv} kV${o.free ? ' · free' : ''}`);
  if (o.table === 'line') rows.push(`${o.std_type || 'line'} · ${o.length_km != null ? `${(o.length_km * 1000).toFixed(0)} m` : ''}`);
  if (o.table === 'trafo') rows.push(`${o.sn_mva} MVA · ${o.vn_hv_kv}/${o.vn_lv_kv} kV`);
  if (o.table === 'switch') rows.push(o.closed ? 'closed' : 'open');
  if (o.p_mw != null) rows.push(`${(o.p_mw * 1000).toFixed(1)} kW`);
  if (o.in_service === false) rows.push('out of service');
  return (
    <div className="nl-tooltip" style={{ left: hover.x + 14, top: hover.y + 14 }}>
      <div className="nl-tt-kind" style={{ color: kindColor(o.kind) }}>{kindLabel(o.kind)}</div>
      <div className="nl-tt-name">{o.name}</div>
      {rows.map((r, i) => <div key={i} className="nl-tt-row">{r}</div>)}
      {pfRow && <div className="nl-tt-row nl-tt-pf">{pfRow}</div>}
    </div>
  );
}

export default function NetLabEditor({ initial, onClose }) {
  const deckRef = useRef(null);
  const mapBoxRef = useRef(null);
  const [session, setSession] = useState(initial.session);
  const [view, setView] = useState(() => normalizeView(initial.view));
  const [version, setVersion] = useState(0);
  const [viewState, setViewState] = useState(null);
  const [selection, setSelection] = useState([]);
  const [filter, setFilter] = useState('all');
  const [hover, setHover] = useState(null);
  const [menu, setMenu] = useState(null);
  const [draft, setDraft] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [pick, setPick] = useState(null);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);
  const [crsDismissed, setCrsDismissed] = useState(false);
  const [pfOn, setPfOn] = useState(false);
  const [pf, setPf] = useState(null);
  const [pfRunning, setPfRunning] = useState(false);
  const [pfError, setPfError] = useState(null);
  const [pfScenario, setPfScenario] = useState({ kind: 'saved', step: null });
  const [pfTimeline, setPfTimeline] = useState(null);
  const pfRequest = useRef(0);
  const sid = session.id;

  // ── Initial camera ────────────────────────────────────────────────────────
  useLayoutEffect(() => {
    const el = mapBoxRef.current;
    const w = el?.clientWidth || window.innerWidth, h = el?.clientHeight || window.innerHeight;
    setViewState({ ...fitBounds(view.bounds, w, h), pitch: 0, bearing: 0 });
    // Re-fit only when the geography changes (a CRS correction), not on edits.
  }, [session.crs]);

  const notify = useCallback((text, kind = 'ok') => {
    setToast({ text, kind, at: Date.now() });
  }, []);
  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), toast.kind === 'error' ? 6000 : 3000);
    return () => clearTimeout(t);
  }, [toast]);

  // ── Applying backend results ──────────────────────────────────────────────
  const apply = useCallback((res) => {
    const v = normalizeView(res.view);
    setSession(res.session);
    setView(v);
    setVersion(x => x + 1);
    setSelection(sel => {
      let next = sel.filter(s => v.byKey.has(s.key));
      const created = res.created || [];
      const main = created[created.length - 1];
      if (main) {
        const key = keyOf(main.table, main.id);
        if (!next.some(s => s.key === key)) next = [...next, { table: main.table, id: main.id, key }];
      }
      return next;
    });
  }, []);

  const run = useCallback(async (fn, message) => {
    setBusy(true);
    try {
      const res = await fn();
      apply(res);
      if (message) notify(typeof message === 'function' ? message(res) : message);
      return res;
    } catch (e) {
      notify(e.message, 'error');
      return null;
    } finally {
      setBusy(false);
    }
  }, [apply, notify]);

  const undo = useCallback(() => {
    if (!session.can_undo || busy) return;
    run(() => netlabApi.undo(sid), 'Undone');
  }, [session.can_undo, busy, run, sid]);

  // ── Power flow layer ──────────────────────────────────────────────────────
  // Re-run after every change to the net while the layer is on; only the
  // newest request may update the map, so quick successive edits never show
  // stale results.
  useEffect(() => {
    if (!pfOn) { setPf(null); setPfError(null); return; }
    const req = ++pfRequest.current;
    setPfRunning(true);
    netlabApi.powerflow(sid, pfScenario.kind, pfScenario.step)
      .then(res => { if (req === pfRequest.current) { setPf(indexResults(res.powerflow)); setPfError(null); } })
      .catch(e => { if (req === pfRequest.current) { setPf(null); setPfError(e.message); } })
      .finally(() => { if (req === pfRequest.current) setPfRunning(false); });
  }, [pfOn, version, sid, pfScenario]);

  // Edits can add or drop profiles, so the scenario timeline follows the net too.
  useEffect(() => {
    if (!pfOn) return undefined;
    let live = true;
    netlabApi.powerflowTimeline(sid)
      .then(res => {
        if (!live) return;
        setPfTimeline(res.timeline);
        if (!res.timeline) setPfScenario(sc => (sc.kind === 'saved' ? sc : { kind: 'saved', step: null }));
      })
      .catch(() => { if (live) setPfTimeline(null); });
    return () => { live = false; };
  }, [pfOn, version, sid]);

  // ── Selection ─────────────────────────────────────────────────────────────
  const toggleSelect = useCallback((o) => {
    setSelection(sel => (sel.some(s => s.key === o.key)
      ? sel.filter(s => s.key !== o.key)
      : [...sel, { table: o.table, id: o.id, key: o.key }]));
  }, []);
  const selectRef = useCallback(({ table, id }) => {
    const key = keyOf(table, id);
    setSelection(sel => (sel.some(s => s.key === key) ? sel : [...sel, { table, id, key }]));
  }, []);
  const selectedKeys = useMemo(() => new Set(selection.map(s => s.key)), [selection]);

  const onRemove = useCallback((items) => run(
    () => netlabApi.op(sid, { op: 'remove', items: items.map(i => ({ table: i.table, id: i.id })) }),
    (res) => `Removed ${res.removed?.length ?? items.length} element${(res.removed?.length ?? items.length) === 1 ? '' : 's'}`,
  ), [run, sid]);

  const onModify = useCallback((item, values) => run(
    () => netlabApi.op(sid, { op: 'modify', table: item.table, id: item.id, values }), 'Saved',
  ), [run, sid]);

  const zoomTo = useCallback((obj) => {
    if (!obj) return;
    const el = mapBoxRef.current;
    let target;
    if (obj.path) {
      const xs = obj.path.map(p => p[0]), ys = obj.path.map(p => p[1]);
      target = fitBounds([Math.min(...xs), Math.min(...ys), Math.max(...xs), Math.max(...ys)],
        el.clientWidth, el.clientHeight, 120);
      target.zoom = Math.min(target.zoom, 19);
    } else {
      target = { longitude: obj.pos[0], latitude: obj.pos[1], zoom: Math.max(viewState?.zoom || 0, 18) };
    }
    setViewState(vs => ({ ...vs, ...target, transitionDuration: 500 }));
  }, [viewState]);

  // ── Adding ────────────────────────────────────────────────────────────────
  const startPick = useCallback((p) => { setMenu(null); setPick(p); }, []);

  const openDraft = useCallback(async (d) => {
    setMenu(null);
    setPick(null);
    try {
      const c = await netlabApi.catalog(sid);
      setCatalog(c.catalog);
      const { pickTo, ...rest } = d;
      setDraft(rest);
      if (pickTo) {
        const from = view.busById.get(rest.from_bus);
        setPick({
          field: 'to_bus', filter: 'bus', prompt: 'Click the bus to connect to',
          accept: (o) => o.table === 'bus' && o.id !== rest.from_bus && (!from || o.vn_kv === from.vn_kv),
          onPick: (o) => setDraft(x => ({ ...x, to_bus: o.id })),
        });
      }
    } catch (e) {
      notify(e.message, 'error');
    }
  }, [sid, view, notify]);

  const submitDraft = useCallback(async (spec) => {
    const res = await run(() => netlabApi.op(sid, { op: 'add', spec }),
      (r) => `Added ${kindLabel(spec.kind)}${r.created?.length > 1 ? ' with a new bus and cable' : ''}`);
    if (res) { setDraft(null); setPick(null); }
  }, [run, sid]);

  // ── Map interaction ───────────────────────────────────────────────────────
  const onMapClick = useCallback((info) => {
    setMenu(null);
    const o = info.object;
    if (pick) {
      if (o && pick.accept(o)) { pick.onPick(o); setPick(null); }
      return;
    }
    if (o) toggleSelect(o);
  }, [pick, toggleSelect]);

  const onContextMenu = useCallback((e) => {
    e.preventDefault();
    const deck = deckRef.current;
    if (!deck || pick) return;
    const rect = mapBoxRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left, y = e.clientY - rect.top;
    const picked = deck.pickObject({ x, y, radius: 6 });
    const viewport = deck.deck.getViewports()[0];
    const pos = viewport.unproject([x, y]);
    setHover(null);
    setMenu({ x, y, pos: [pos[0], pos[1]], object: picked?.object || null });
  }, [pick]);

  useEffect(() => {
    const onKey = (e) => {
      const typing = ['INPUT', 'SELECT', 'TEXTAREA'].includes(document.activeElement?.tagName);
      if (e.key === 'Escape') {
        if (pick) setPick(null);
        else if (menu) setMenu(null);
      }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z' && !typing) {
        e.preventDefault();
        undo();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pick, menu, undo]);

  // ── Layers ────────────────────────────────────────────────────────────────
  const zoom = viewState?.zoom ?? 14;
  const iconSize = iconSizeFor(zoom);
  const activeFilter = pick ? pick.filter : filter;

  const split = useCallback((arr) => {
    const on = [], off = [];
    for (const o of arr) {
      (matchesFilter(o, activeFilter) && (!pick || pick.accept(o)) ? on : off).push(o);
    }
    return [on, off];
  }, [activeFilter, pick]);

  // Fan single-bus elements out around their bus so each stays clickable.
  const elementOffsets = useMemo(() => {
    const byBus = {};
    for (const e of view.elements) (byBus[e.bus] = byBus[e.bus] || []).push(e);
    const out = {};
    const r = iconSize * 0.95 + 3;
    for (const list of Object.values(byBus)) {
      const step = (2 * Math.PI) / Math.max(list.length, 6);
      list.forEach((e, i) => {
        const a = -Math.PI / 2 + i * step;
        out[e.key] = [Math.cos(a) * r, Math.sin(a) * r];
      });
    }
    return out;
  }, [view.elements, iconSize]);

  const layers = useMemo(() => {
    const L = [];
    const [linesOn, linesOff] = split(view.lines);
    const [busesOn, busesOff] = split(view.buses);
    const [trafosOn, trafosOff] = split(view.trafos);
    const [switchesOn, switchesOff] = split(view.switches);
    const [elementsOn, elementsOff] = split(view.elements);
    const lineSev = (l) => loadingSeverity(pf?.line.get(l.id));
    const lineColor = (l, a) => (!l.in_service ? [120, 110, 140, a]
      : pf ? [...severityColor(lineSev(l)), a] : [...voltageColor(l.vn_kv), a]);
    const baseWidth = (l) => (l.vn_kv >= 60 ? 4 : l.vn_kv >= 1 ? 3 : 2);
    const lineWidth = (l) => (pf ? baseWidth(l) + 1 + ((lineSev(l) ?? 0) > 1 ? 3 : 0) : baseWidth(l));

    L.push(new PathLayer({
      id: 'lines-off', data: linesOff, getPath: l => l.path, getColor: l => lineColor(l, OFF_ALPHA),
      getWidth: lineWidth, widthUnits: 'pixels', widthMinPixels: 1, pickable: false,
      updateTriggers: { getColor: [activeFilter, pf], getWidth: [pf] },
    }));
    const selLines = view.lines.filter(l => selectedKeys.has(l.key));
    if (selLines.length) {
      L.push(new PathLayer({
        id: 'lines-selected', data: selLines, getPath: l => l.path, getColor: [255, 255, 255, 120],
        getWidth: 10, widthUnits: 'pixels', capRounded: true, jointRounded: true, pickable: false,
      }));
    }
    L.push(new PathLayer({
      id: 'lines', data: linesOn, getPath: l => l.path,
      getColor: l => (selectedKeys.has(l.key) ? [255, 255, 255, 255] : lineColor(l, 235)),
      getWidth: lineWidth, widthUnits: 'pixels', widthMinPixels: 2, capRounded: true, jointRounded: true,
      pickable: true, autoHighlight: true, highlightColor: [255, 255, 255, 230],
      updateTriggers: { getColor: [selection, pf], getWidth: [pf] },
    }));

    // Preview cable for a draft that creates a new bus.
    if (draft?.pos) {
      const fromId = draft.kind === 'trafo' ? draft.hv_bus : draft.bus;
      const from = fromId != null ? view.busById.get(fromId) : null;
      const cable = from && (draft.connect === 'new_bus' || (draft.kind === 'bus' && draft.connectBus));
      if (cable) {
        L.push(new LineLayer({
          id: 'draft-cable', data: [{ a: from.pos, b: draft.pos }], getSourcePosition: d => d.a,
          getTargetPosition: d => d.b, getColor: [216, 180, 254, 220], getWidth: 2, widthUnits: 'pixels',
        }));
      }
    }

    const busSev = (b) => busSeverity(pf?.bus.get(b.id));
    const busStyle = {
      getPosition: b => b.pos, radiusUnits: 'pixels', stroked: true, lineWidthUnits: 'pixels',
      getRadius: b => (b.vn_kv >= 1 ? 4.5 : 3.2) * (iconSize >= 14 ? 1.25 : 1) * (pf ? ((busSev(b) ?? 0) > 1 ? 1.8 : 1.3) : 1),
      getLineWidth: 1.2,
    };
    const busFill = (b) => (!b.in_service ? [120, 110, 140, 220]
      : pf ? [...severityColor(busSev(b)), 255]
        : b.free ? [26, 14, 52, 255] : [...voltageColor(b.vn_kv), 255]);
    L.push(new ScatterplotLayer({
      id: 'buses-off', data: busesOff, ...busStyle, pickable: false,
      getFillColor: b => [...(pf ? severityColor(busSev(b)) : voltageColor(b.vn_kv)), OFF_ALPHA], getLineColor: [20, 10, 40, OFF_ALPHA],
      updateTriggers: { getRadius: [iconSize, pf], getFillColor: [pf] },
    }));
    L.push(new ScatterplotLayer({
      id: 'buses', data: busesOn, ...busStyle, pickable: true, autoHighlight: true,
      highlightColor: [255, 255, 255, 255],
      getFillColor: busFill,
      getLineColor: b => (b.free && !pf ? [...voltageColor(b.vn_kv), 255] : [20, 10, 40, 255]),
      updateTriggers: { getRadius: [iconSize, pf], getFillColor: [pf], getLineColor: [pf] },
    }));

    const iconLayer = (id, data, getKind, extra = {}) => new IconLayer({
      id, data, getPosition: o => o.pos, sizeUnits: 'pixels', getSize: iconSize,
      getIcon: o => { const [k, v] = getKind(o); return iconDef(iconUrl(k, v), `${k}:${v || ''}`); },
      updateTriggers: { getSize: [iconSize], getPixelOffset: [elementOffsets] }, ...extra,
    });
    const dim = { getColor: [255, 255, 255, 60], pickable: false };
    const live = { getColor: o => [255, 255, 255, o.in_service === false ? 120 : 255], pickable: true, autoHighlight: true, highlightColor: [255, 255, 255, 90] };
    const swKind = s => ['switch', s.closed ? undefined : 'open'];
    const elOffset = { getPixelOffset: e => elementOffsets[e.key] || [0, 0] };

    if (pf) {
      L.push(new ScatterplotLayer({
        id: 'trafo-loading', data: view.trafos, getPosition: t => t.pos, radiusUnits: 'pixels',
        getRadius: iconSize * 0.9 + 4, pickable: false,
        getFillColor: t => [...severityColor(loadingSeverity(pf.trafo.get(t.id))), 230],
        updateTriggers: { getFillColor: [pf], getRadius: [iconSize] },
      }));
    }

    L.push(iconLayer('switches-off', switchesOff, swKind, { ...dim, getSize: iconSize * 0.8 }));
    L.push(iconLayer('switches', switchesOn, swKind, { ...live, getSize: iconSize * 0.8 }));
    L.push(iconLayer('elements-off', elementsOff, e => [e.kind], { ...dim, ...elOffset }));
    L.push(iconLayer('elements', elementsOn, e => [e.kind], { ...live, ...elOffset }));
    L.push(iconLayer('trafos-off', trafosOff, () => ['trafo'], { ...dim, getSize: iconSize * 1.25 }));
    L.push(iconLayer('trafos', trafosOn, () => ['trafo'], { ...live, getSize: iconSize * 1.25 }));

    // Selection rings on every selected point asset.
    const selPoints = selection.map(s => view.byKey.get(s.key)).filter(o => o && o.pos && !o.path);
    if (selPoints.length) {
      L.push(new IconLayer({
        id: 'selection-rings', data: selPoints, getPosition: o => o.pos, sizeUnits: 'pixels',
        getIcon: () => iconDef(SELECTION_RING, 'ring'),
        getSize: o => (o.table === 'bus' ? Math.max(14, iconSize) : o.table === 'trafo' ? iconSize * 1.25 : iconSize) + 8,
        getPixelOffset: o => (o.table !== 'bus' && o.table !== 'trafo' && o.table !== 'switch' ? elementOffsets[o.key] || [0, 0] : [0, 0]),
        pickable: false, updateTriggers: { getSize: [iconSize], getPixelOffset: [elementOffsets] },
      }));
    }

    if (draft?.pos) {
      L.push(new IconLayer({
        id: 'draft-pin', data: [draft], getPosition: d => d.pos, sizeUnits: 'pixels', getSize: 34,
        getIcon: () => ({ url: DRAFT_PIN, id: 'pin', width: 64, height: 64, anchorY: 60 }),
      }));
    }
    return L;
  }, [view, split, activeFilter, selectedKeys, selection, iconSize, elementOffsets, draft, pf]);

  // ── Chrome ────────────────────────────────────────────────────────────────
  const counts = view.counts || {};
  const chips = FILTERS.filter(f => f.key === 'all' || (counts[f.key] || 0) > 0);
  const menuObj = menu?.object;

  return (
    <div className="nl-editor">
      <header className="nl-topbar">
        <img src={LOGO_URL} alt="NetLab" className="nl-top-logo" />
        <div className="nl-top-title">
          <div className="nl-top-name" title={session.name}>{session.name}</div>
          <div className="nl-top-meta">
            {counts.bus || 0} buses · {counts.line || 0} lines · {(counts.load || 0) + (counts.ev_charger || 0) + (counts.heat_pump || 0)} loads
            {session.profiles?.count ? ` · ${session.profiles.count} profiles (${session.profiles.timeline?.step})` : ''}
            · {session.crs}
          </div>
        </div>
        <div className="nl-top-actions">
          <button className="nl-btn ghost" onClick={undo} disabled={!session.can_undo || busy} title="Undo (Ctrl+Z)">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2"
              strokeLinecap="round" strokeLinejoin="round"><path d="M9 14 4 9l5-5" /><path d="M4 9h10.5a5.5 5.5 0 0 1 0 11H11" /></svg>
            Undo{session.undo_depth ? ` (${session.undo_depth})` : ''}
          </button>
          <button className={`nl-btn${pfOn ? ' active' : ' ghost'}`} onClick={() => setPfOn(on => !on)}
            aria-pressed={pfOn} title="Colour the grid by power-flow results (re-runs after every change)">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2"
              strokeLinecap="round" strokeLinejoin="round"><path d="M13 2 4 14h7l-1 8 9-12h-7z" /></svg>
            Power flow
          </button>
          <button className="nl-btn primary" disabled={busy}
            onClick={() => netlabApi.download(sid, session.name).catch(e => notify(e.message, 'error'))}>
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2"
              strokeLinecap="round" strokeLinejoin="round"><path d="M12 4v11M7 10l5 5 5-5" /><path d="M5 20h14" /></svg>
            {session.profiles?.count ? 'Download ZIP' : 'Download JSON'}
          </button>
          <button className="nl-btn ghost" onClick={onClose}>Close</button>
        </div>
      </header>

      <div className="nl-body">
        <div className={`nl-map${pick ? ' picking' : ''}`} ref={mapBoxRef} onContextMenu={onContextMenu}>
          {viewState && (
            <DeckGL
              ref={deckRef}
              viewState={viewState}
              onViewStateChange={({ viewState: vs }) => setViewState(vs)}
              controller={{ dragRotate: false, touchRotate: false, keyboard: false }}
              layers={layers}
              pickingRadius={6}
              onClick={onMapClick}
              onHover={(info) => setHover(info.object && !menu ? { object: info.object, x: info.x, y: info.y } : null)}
              getCursor={({ isDragging, isHovering }) => (isDragging ? 'grabbing' : isHovering ? 'pointer' : pick ? 'crosshair' : 'grab')}
            >
              <Map mapStyle={MAP_STYLE} />
            </DeckGL>
          )}

          <div className="nl-filterbar" role="toolbar" aria-label="Filter assets">
            {chips.map(f => (
              <button key={f.key} title={f.hint || `Only ${f.label.toLowerCase()} are clickable`}
                className={`nl-filter${filter === f.key ? ' active' : ''}`}
                onClick={() => setFilter(f.key)} disabled={!!pick}>
                {f.key !== 'all' && <img src={iconUrl(f.key === 'free_bus' ? 'bus' : f.key)} alt="" />}
                {f.label}
                {f.key !== 'all' && <span className="nl-count">{counts[f.key]}</span>}
              </button>
            ))}
          </div>

          {session.crs_guessed && !crsDismissed && (
            <div className="nl-banner">
              Bus positions were assumed to be in <b>{session.crs}</b>. Grid in the wrong place?
              {UTM_CHOICES.map(([code, label]) => (
                <button key={code} className={`nl-btn small${session.crs === code ? ' active' : ''}`} disabled={busy}
                  onClick={() => run(() => netlabApi.setCrs(sid, code), `Coordinates read as ${label}`)}>
                  {label}
                </button>
              ))}
              <button className="nl-icon-btn" onClick={() => setCrsDismissed(true)} aria-label="Dismiss">×</button>
            </div>
          )}

          {pick && (
            <div className="nl-pickbanner">
              <span className="nl-pulse" /> {pick.prompt} <kbd>Esc</kbd> to cancel
            </div>
          )}

          {!pick && !menu && <Tooltip hover={hover} pf={pf} />}

          {pfOn && <PowerFlowLegend pf={pf} running={pfRunning} error={pfError} timeline={pfTimeline}
            scenario={pfScenario} onScenario={setPfScenario} />}

          {menu && (
            <div className="nl-menu" style={{ left: menu.x, top: menu.y }} onContextMenu={(e) => e.preventDefault()}>
              {menuObj && (
                <>
                  <div className="nl-menu-head">
                    <img src={iconUrl(menuObj.kind)} alt="" /> {menuObj.name}
                  </div>
                  <button onClick={() => { toggleSelect(menuObj); setMenu(null); }}>
                    {selectedKeys.has(menuObj.key) ? 'Deselect' : 'Select'}
                  </button>
                  {menuObj.table === 'bus' && (
                    <button onClick={() => openDraft({ kind: 'load', bus: menuObj.id })}>Add asset to this bus…</button>
                  )}
                  {menuObj.table === 'line' && (
                    <button onClick={() => openDraft({ kind: 'switch', element: menuObj.id, bus: menuObj.from_bus })}>Add switch…</button>
                  )}
                  <button className="danger" onClick={() => { setMenu(null); onRemove([menuObj]); }}>Remove</button>
                  <div className="nl-menu-sep" />
                </>
              )}
              <button onClick={() => openDraft({ kind: draft?.kind && draft.kind !== 'switch' ? draft.kind : 'load', pos: menu.pos })}>
                <b>Add asset here…</b>
              </button>
            </div>
          )}

          {toast && <div className={`nl-toast ${toast.kind}`}>{toast.text}</div>}

          <div className="nl-hintbar">
            Click to select · Right-click to add · Scroll to zoom
          </div>
        </div>

        <aside className="nl-panel">
          {draft && catalog && (
            <AddAssetForm sid={sid} catalog={catalog} view={view} draft={draft} setDraft={setDraft} busy={busy}
              pick={pick} startPick={startPick} onSubmit={submitDraft}
              onCancel={() => { setDraft(null); setPick(null); }} />
          )}
          <SelectionPanel sid={sid} selection={selection} version={version} view={view} busy={busy}
            onClear={() => setSelection([])}
            onDeselect={(item) => setSelection(sel => sel.filter(s => s.key !== item.key))}
            onSelectRef={selectRef} onRemove={onRemove} onModify={onModify} onStartAdd={openDraft}
            onZoom={zoomTo} />
          {!draft && !selection.length && (
            <div className="nl-empty">
              <h3>Nothing selected</h3>
              <ul>
                <li><b>Click</b> assets to open them here. Every click adds a card, so you can compare and edit several at once.</li>
                <li><b>Right-click</b> anywhere on the map to add a load, PV, wind turbine, EV charger, transformer, switch…</li>
                <li>Use the <b>filters</b> to make only one kind of asset clickable, e.g. only <i>free buses</i> or only <i>lines</i>.</li>
                <li>New loads, PV, wind, EV chargers, heat pumps and batteries can get a <b>profile</b>: from this NetBank area, copied from another asset, a standard shape, or your own file.</li>
                <li><b>Ctrl+Z</b> undoes the last change. <b>Download</b> exports the edited pandapower net, plus NetGen-style profile files when there are profiles.</li>
              </ul>
              <div className="nl-legend">
                {[['LV', voltageColor(0.4)], ['MV', voltageColor(20)], ['HV', voltageColor(110)]].map(([l, c]) => (
                  <span key={l}><i style={{ background: `rgb(${c.join(',')})` }} />{l} lines</span>
                ))}
                <span><i className="ring" style={{ borderColor: `rgb(${hexToRgb('#ddd6fe').join(',')})` }} />free bus</span>
              </div>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
